/**
 * Product Release queries — PRD.206.
 *
 * Product Release is the stock-RELEASE workflow for `Product` inventory
 * items handed to the patient at the front desk / cashier after invoice
 * payment. Mirrors the Pharmacy queue pattern but is intentionally
 * separate so Pharmacy queues stay Medicine-only.
 *
 * The stock-out event is `confirmRelease`; this module exposes only
 * reads. The actual write path lives in the invoice page (PRD.206 §15)
 * and creates rows in:
 *   - product_releases
 *   - product_release_lines
 *   - stock_movements (source_type='product_release')
 *
 * Eligibility rule for v1:
 *   invoices.status = 'paid'
 *   AND invoice_items.inventory_item_id IS NOT NULL
 *   AND inventory_items.item_type = 'product'
 *   AND COALESCE(SUM(confirmed product_release_lines.quantity_released), 0)
 *       < invoice_items.qty
 */

import type { QuerySpec } from '@helios/powersync-client-core';

// ─── Row types ───

export interface ProductReleaseRow {
  id: string;
  org_id: string | null;
  release_number: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  invoice_id: string | null;
  status: string | null; // 'draft' | 'confirmed' | 'cancelled'
  released_at: string | null;
  released_by: string | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ProductReleaseLineRow {
  id: string;
  org_id: string | null;
  product_release_id: string | null;
  invoice_item_id: string | null;
  inventory_item_id: string | null;
  inventory_batch_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  batch_number_snapshot: string | null;
  expiry_date_snapshot: string | null;
  unit_snapshot: string | null;
  instructions_snapshot: string | null;
  quantity_to_release: number | null;
  quantity_released: number | null;
  status: string | null;
  remarks: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * Per-invoice-item Product release status row, joined with the line's
 * inventory item type so the UI can branch on Product vs Medicine vs
 * Service vs Manual without re-querying inventory.
 *
 * Status mapping is computed in the consumer (InvoicesTab) so this query
 * stays stable as future statuses are added:
 *   inventory_item_id IS NULL OR item_type != 'product'  → not_product
 *   item_type = 'product' AND invoice.status != 'paid'   → awaiting_payment
 *   total_released = 0 AND qty > 0                       → ready_for_release
 *   0 < total_released < qty                              → partially_released
 *   total_released >= qty                                  → released
 */
export interface InvoiceItemProductReleaseStatusRow {
  invoice_item_id: string;
  invoice_id: string | null;
  qty: number | null;
  inventory_item_id: string | null;
  item_type: string | null;
  total_released: number;
}

export function qInvoiceProductReleaseStatuses(
  invoiceId: string,
): QuerySpec<[string]> {
  return {
    key: ['invoice-product-release-statuses', invoiceId] as const,
    sql: `
      SELECT
        ii.id                AS invoice_item_id,
        ii.invoice_id        AS invoice_id,
        ii.qty               AS qty,
        ii.inventory_item_id AS inventory_item_id,
        inv.item_type        AS item_type,
        COALESCE((
          SELECT SUM(COALESCE(prl.quantity_released, 0))
          FROM product_release_lines prl
          WHERE prl.invoice_item_id = ii.id
            AND prl.status = 'confirmed'
        ), 0) AS total_released
      FROM invoice_items ii
      LEFT JOIN inventory_items inv ON inv.id = ii.inventory_item_id
      WHERE ii.invoice_id = ?
      ORDER BY ii.sort_order ASC, ii.created_at ASC
    `,
    params: [invoiceId],
    watch: ['invoice_items', 'inventory_items', 'product_release_lines'],
  };
}

/**
 * All confirmed release lines for a given invoice item. Use to drive
 * duplicate-release detection or to render the per-line release history
 * underneath an invoice row.
 */
export function qProductReleaseLinesByInvoiceItem(
  invoiceItemId: string,
): QuerySpec<[string]> {
  return {
    key: ['product-release-lines-by-invoice-item', invoiceItemId] as const,
    sql: `
      SELECT * FROM product_release_lines
      WHERE invoice_item_id = ?
      ORDER BY created_at ASC
    `,
    params: [invoiceItemId],
    watch: ['product_release_lines'],
  };
}

/**
 * One row per confirmed release line for an invoice, with header
 * metadata + batch metadata for the invoice-scoped release history
 * panel (PRD.206 §18).
 */
export interface InvoiceProductReleaseHistoryRow {
  product_release_id: string | null;
  product_release_line_id: string;
  release_number: string | null;
  released_at: string | null;
  released_by: string | null;
  invoice_item_id: string | null;
  inventory_item_id: string | null;
  item_name_snapshot: string | null;
  sku_snapshot: string | null;
  batch_number_snapshot: string | null;
  expiry_date_snapshot: string | null;
  unit_snapshot: string | null;
  quantity_released: number | null;
  remarks: string | null;
}

export function qProductReleaseHistoryByInvoice(
  invoiceId: string,
): QuerySpec<[string]> {
  return {
    key: ['product-release-history-by-invoice', invoiceId] as const,
    sql: `
      SELECT
        pr.id                        AS product_release_id,
        prl.id                       AS product_release_line_id,
        pr.release_number            AS release_number,
        pr.released_at               AS released_at,
        pr.released_by               AS released_by,
        prl.invoice_item_id          AS invoice_item_id,
        prl.inventory_item_id        AS inventory_item_id,
        prl.item_name_snapshot       AS item_name_snapshot,
        prl.sku_snapshot             AS sku_snapshot,
        prl.batch_number_snapshot    AS batch_number_snapshot,
        prl.expiry_date_snapshot     AS expiry_date_snapshot,
        prl.unit_snapshot            AS unit_snapshot,
        prl.quantity_released        AS quantity_released,
        prl.remarks                  AS remarks
      FROM product_release_lines prl
      JOIN product_releases pr ON pr.id = prl.product_release_id
      WHERE pr.invoice_id = ?
        AND prl.status = 'confirmed'
      ORDER BY pr.released_at DESC, prl.created_at ASC
    `,
    params: [invoiceId],
    watch: ['product_releases', 'product_release_lines'],
  };
}

/**
 * Existing batches available for releasing a given Product, with
 * computed available quantity from the stock-movements ledger. Mirror of
 * the Pharmacy `qAvailableBatchesForDispense` helper so the UI can reuse
 * the same picker pattern.
 *
 * Product Release must NOT create new batches — this query is the
 * entire universe of selectable batches at release time.
 */
export interface ProductReleaseBatchOptionRow {
  inventory_batch_id: string;
  batch_number: string | null;
  expiry_date: string | null;
  available_quantity: number | null;
}

export function qAvailableBatchesForProductRelease(
  itemId: string,
): QuerySpec<[string]> {
  return {
    key: ['product-release-available-batches', itemId] as const,
    sql: `
      SELECT
        b.id                                                           AS inventory_batch_id,
        b.batch_number                                                  AS batch_number,
        b.expiry_date                                                   AS expiry_date,
        COALESCE((
          SELECT
            COALESCE(SUM(COALESCE(sm.quantity_in, 0)), 0)
              - COALESCE(SUM(COALESCE(sm.quantity_out, 0)), 0)
          FROM stock_movements sm
          WHERE sm.inventory_batch_id = b.id
        ), 0)                                                            AS available_quantity
      FROM inventory_batches b
      WHERE b.inventory_item_id = ?
      ORDER BY
        CASE WHEN b.expiry_date IS NULL THEN 1 ELSE 0 END,
        b.expiry_date ASC,
        b.batch_number ASC
    `,
    params: [itemId],
    watch: ['inventory_batches', 'stock_movements'],
  };
}
